/**
 * @file Services de progression secondaires : passe de saison, cadeau quotidien 7 jours, tickets de modes, deblocages, tutoriel.
 */
import { MODE_IDS } from '@shared/enums';
import { fail, okVoid } from '@shared/types';
import type { Result } from '@shared/types';
import type { TicketId, UnlockRule } from '@config/types';
import { TUTORIAL_STEPS } from '@config/unlocks';
import type { FeatureId } from '@config/unlocks';
import { dayKey } from '../Clock';
import type { SimContext } from '../Context';

/** Passe de saison : 25 paliers, piste gratuite + piste "epic" (debloquee en Dia), saisons de 28 jours. */
export class PassService {
  constructor(private readonly ctx: SimContext) {}

  /** Palier atteint (0..25) selon l'XP. */
  currentTier(): number {
    const p = this.ctx.config.pass;
    return Math.min(p.tierCount, Math.floor(this.ctx.data.pass.xp / p.xpPerTier));
  }

  /** xp += round(amount x stat xpGain), plafonne a tierCount x xpPerTier. */
  addXp(amount: number): void {
    const p = this.ctx.config.pass;
    const gain = Math.round(amount * this.ctx.stats.getNumber('xpGain'));
    this.ctx.data.pass.xp = Math.min(p.tierCount * p.xpPerTier, this.ctx.data.pass.xp + gain);
  }

  claim(tier: number, track: 'free' | 'epic'): Result {
    const ctx = this.ctx;
    const pass = ctx.data.pass;
    const cfg = ctx.config.pass;
    if (tier < 1 || tier > cfg.tierCount || tier > this.currentTier()) return fail('locked');
    if (track === 'epic' && !pass.epicUnlocked) return fail('locked');
    const claimed = track === 'free' ? pass.claimedFree : pass.claimedEpic;
    if (claimed.includes(tier)) return fail('nothingToClaim');
    const def = cfg.tiers[tier - 1];
    if (!def) return fail('unknownId');
    claimed.push(tier);
    ctx.tx.grantBundle(def[track], `pass:${track}:${tier}`);
    ctx.bus.emit('PassTierClaimed', { tier, track });
    return okVoid;
  }

  unlockEpic(): Result {
    const ctx = this.ctx;
    if (ctx.data.pass.epicUnlocked) return fail('alreadyOwned');
    return ctx.tx.purchase(ctx.config.pass.epicUnlockCost, 'passEpic', () => {
      ctx.data.pass.epicUnlocked = true;
      return okVoid;
    });
  }

  /** Nouvelle saison : xp, paliers reclames et piste epic remis a zero. */
  refreshSeason(nowMs: number): void {
    const pass = this.ctx.data.pass;
    const id = Math.floor(dayKey(nowMs) / this.ctx.config.pass.seasonDays);
    if (pass.seasonId === id) return;
    const first = pass.seasonId < 0;
    pass.seasonId = id;
    if (first) return;
    pass.xp = 0;
    pass.claimedFree = [];
    pass.claimedEpic = [];
    pass.epicUnlocked = false;
  }
}

/** Cadeau de connexion : un par jour UTC, cycle de 7 jours (le cycle continue meme si on rate un jour). */
export class DailyService {
  constructor(private readonly ctx: SimContext) {}

  canClaim(): boolean {
    return this.ctx.data.daily.lastClaimDayKey !== dayKey(this.ctx.rt.wallMs);
  }

  claim(): Result {
    const ctx = this.ctx;
    if (!this.canClaim()) return fail('nothingToClaim');
    const d = ctx.data.daily;
    const day = d.nextDay;
    const reward = ctx.config.dailyRewards[day - 1];
    if (!reward) return fail('unknownId');
    d.nextDay = (day % 7) + 1;
    d.lastClaimDayKey = dayKey(ctx.rt.wallMs);
    ctx.tx.grantBundle(reward.reward, `daily:${day}`);
    ctx.bus.emit('DailyClaimed', { day });
    return okVoid;
  }
}

/** Tickets des modes secondaires : stock plafonne, regeneration temporelle (Adventure : max 20, +1 toutes les 4 h). */
export class TicketService {
  constructor(private readonly ctx: SimContext) {}

  count(id: TicketId): number {
    return this.ctx.data.tickets[id].count;
  }

  /** count-- ; si le stock etait au max, la regeneration redemarre maintenant. */
  consume(id: TicketId): Result {
    const st = this.ctx.data.tickets[id];
    if (st.count <= 0) return fail('noTickets');
    if (st.count >= this.ctx.config.balance.tickets[id].max) st.lastRegenMs = this.ctx.rt.wallMs;
    st.count--;
    return okVoid;
  }

  /** Ajoute des tickets (recompenses) ; peut depasser le max (regeneration bloquee tant que >= max). */
  grant(t: Partial<Record<TicketId, number>>): void {
    for (const [id, n] of Object.entries(t) as Array<[TicketId, number | undefined]>) {
      if (n && n > 0) this.ctx.data.tickets[id].count += n;
    }
  }

  /** Regenere les tickets ecoules ; retourne le total regenere. */
  regen(nowMs: number): number {
    let total = 0;
    for (const id of Object.keys(this.ctx.config.balance.tickets) as TicketId[]) {
      const spec = this.ctx.config.balance.tickets[id];
      const st = this.ctx.data.tickets[id];
      if (st.count >= spec.max) {
        st.lastRegenMs = nowMs;
        continue;
      }
      const step = spec.regenSeconds * 1000;
      const n = Math.floor((nowMs - st.lastRegenMs) / step);
      if (n <= 0) continue;
      const add = Math.min(n, spec.max - st.count);
      st.count += add;
      total += add;
      st.lastRegenMs = st.count >= spec.max ? nowMs : st.lastRegenMs + n * step;
    }
    return total;
  }

  /** Secondes avant le prochain ticket (Infinity si plein). */
  nextIn(id: TicketId): number {
    const spec = this.ctx.config.balance.tickets[id];
    const st = this.ctx.data.tickets[id];
    if (st.count >= spec.max) return Number.POSITIVE_INFINITY;
    return Math.max(0, spec.regenSeconds - (this.ctx.rt.wallMs - st.lastRegenMs) / 1000);
  }
}

/** Deblocages de fonctionnalites et de modes (UnlockRule data-driven). */
export class UnlockService {
  private readonly announced = new Set<string>();

  constructor(private readonly ctx: SimContext) {
    // Etat de depart : ce qui est deja debloque au chargement n'est pas annonce.
    for (const key of this.unlockedKeys()) this.announced.add(key);
  }

  isUnlocked(rule: UnlockRule): boolean {
    switch (rule.kind) {
      case 'always':
        return true;
      case 'stageValue':
        return this.ctx.data.progress.bestStageValue >= rule.atLeast;
      case 'rebirths':
        return this.ctx.data.rebirth.count >= rule.atLeast;
      case 'flag':
        return this.ctx.data.flags[rule.flag] === true;
    }
  }

  isFeatureUnlocked(f: FeatureId): boolean {
    return this.isUnlocked(this.ctx.config.featureUnlocks[f]);
  }

  private unlockedKeys(): string[] {
    const cfg = this.ctx.config;
    const out: string[] = [];
    for (const f of Object.keys(cfg.featureUnlocks) as FeatureId[]) if (this.isFeatureUnlocked(f)) out.push(`feature:${f}`);
    for (const id of MODE_IDS) if (this.isUnlocked(cfg.modes[id].entry.unlock)) out.push(`mode:${id}`);
    return out;
  }

  /** Annonce (FeatureUnlocked + Toast) les fonctionnalites et modes nouvellement debloques. */
  checkNewUnlocks(): void {
    for (const key of this.unlockedKeys()) {
      if (this.announced.has(key)) continue;
      this.announced.add(key);
      const [kind, id] = key.split(':') as [string, string];
      const name = kind === 'feature' ? `feature.${id}` : `mode.${id}.title`;
      this.ctx.bus.emit('FeatureUnlocked', { feature: key });
      this.ctx.bus.emit('Toast', { key: 'toast.unlocked', params: { name }, kind: 'success' });
    }
  }
}

/** Tutoriel guide en 5 etapes (Upgrade -> Challenge -> GunOpen -> GunBuy -> GunEquip), pilote par les evenements du bus. */
export class TutorialService {
  private offs: Array<() => void> = [];
  /** Evenements deja vus pour l'etape courante (etapes a `count` > 1) ; non persiste : repart de 0 au rechargement. */
  private seen = 0;

  constructor(private readonly ctx: SimContext) {}

  /** S'abonne aux evenements qui terminent chaque etape ; l'etape courante est persistee dans data.tutorial.stepIndex. */
  init(): void {
    const t = this.ctx.data.tutorial;
    if (t.done || this.offs.length > 0) return;
    const bus = this.ctx.bus;
    this.offs = [
      bus.on('UpgradeBought', () => this.complete('UpgradeBought')),
      bus.on('TargetKilled', () => this.complete('TargetKilled')),
      bus.on('ScreenOpened', (e) => {
        if (e.screen === 'weapons') this.complete('ScreenOpened');
      }),
      bus.on('WeaponDrawn', () => this.complete('WeaponDrawn')),
      bus.on('WeaponEquipped', (e) => {
        if (e.uid !== null) this.complete('WeaponEquipped');
      }),
    ];
  }

  private complete(event: (typeof TUTORIAL_STEPS)[number]['completeOn']): void {
    const t = this.ctx.data.tutorial;
    const step = TUTORIAL_STEPS[t.stepIndex];
    if (t.done || !step || step.completeOn !== event) return;
    this.seen++;
    if (this.seen < (step.count ?? 1)) {
      this.ctx.bus.emit('TutorialStep', { stepIndex: t.stepIndex, done: false, progress: this.seen });
      return;
    }
    this.seen = 0;
    t.stepIndex++;
    if (t.stepIndex >= TUTORIAL_STEPS.length) {
      t.done = true;
      this.stop();
    }
    this.ctx.bus.emit('TutorialStep', { stepIndex: t.stepIndex, done: t.done, progress: 0 });
  }

  private stop(): void {
    for (const off of this.offs) off();
    this.offs = [];
  }

  skip(): void {
    const t = this.ctx.data.tutorial;
    t.done = true;
    this.stop();
    this.ctx.bus.emit('TutorialStep', { stepIndex: t.stepIndex, done: true });
  }
}
